import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Dot } from '@/lib/foundations/dot-icon';
import { cx } from '@/utils/cx';

import { hexToRgba } from '../../../utils/colors/hexToRgba';
import { stopClickPropagation } from './stopClickPropagation';
import { useStageLinkData } from './useStageLinkData';

const DEFAULT_STAGE_COLOR = '#7A5AF8'; // utility-brand-500

export interface PipelineStageValueBadgeProps {
  // Data
  /** Stage entity_id (UUID). */
  value: string | null | undefined;
  /** Optional org ID for stage lookup; falls back to OrgContext internally. */
  orgId?: number;
  /**
   * Fallback label to render when the CRM store hasn't loaded the stage yet.
   * Useful in contexts (e.g. Storybook) where there's no live backend.
   */
  fallbackLabel?: string;

  // Presentation
  /** Size variant: 'sm' uses text-xs, 'md' (default) uses text-sm. */
  size?: 'sm' | 'md';
  /**
   * Muted variant: renders with neutral colors (no stage color tint).
   * Use for "current" stage in transition UIs (current → proposed).
   */
  muted?: boolean;
  className?: string;

  // Behavior
  /**
   * When true (default) and the stage's pipeline can be resolved, the badge
   * renders as a link to the pipeline view with `?stage=<id>` — PipelineBoard
   * scrolls the matching column into view. Set false on surfaces where
   * navigating away is wrong (e.g. inside a modal flow like record merge).
   */
  interactive?: boolean;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs font-medium',
  md: 'px-3 py-1 text-sm font-semibold',
};

/**
 * Color-coded stage badge — a rounded pill with the pipeline stage's color
 * as background tint, border, text, and leading dot.
 *
 * This is the canonical "stage chip" for any surface that needs a visually
 * prominent stage indicator (pipeline columns, action cards, record headers).
 * For a plain-text stage label, use `PipelineStageValueLabel` instead.
 *
 * @example
 * <PipelineStageValueBadge value="stage-entity-uuid" />
 * // Renders: [● In Progress] with the stage's pipeline color
 */
export const PipelineStageValueBadge: React.FC<
  PipelineStageValueBadgeProps
> = ({
  value,
  orgId,
  fallbackLabel,
  size = 'md',
  muted = false,
  className = '',
  interactive = true,
}) => {
  // Deep link to the stage's pipeline view. Null while unresolved, in which
  // case the badge stays a plain (non-navigating) pill. `enabled: false`
  // when the badge can't or shouldn't navigate — non-interactive or no
  // value — so opt-out and `(not set)` badges never trigger the pipelines
  // fetch behind useStagePipelineRoute.
  const shouldResolveRoute = interactive && Boolean(value);
  const { stage, isLoading, pipelineRoute } = useStageLinkData(value, {
    orgId,
    enabled: shouldResolveRoute,
  });
  const stageColor = stage?.color || DEFAULT_STAGE_COLOR;
  const stageLabel = stage?.label || fallbackLabel;

  const badgeStyle = useMemo(
    () =>
      muted
        ? undefined
        : {
            backgroundColor: hexToRgba(stageColor, 0.1),
            borderColor: hexToRgba(stageColor, 0.3),
            color: stageColor,
          },
    [stageColor, muted]
  );

  if (!value) {
    return (
      <span className={cx('text-sm text-fg-quaternary italic', className)}>
        (not set)
      </span>
    );
  }

  if (isLoading && !stageLabel) {
    return (
      <span
        className={cx(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm animate-pulse',
          className
        )}
      >
        ...
      </span>
    );
  }

  if (!stageLabel) {
    return (
      <span className={cx('text-sm text-fg-tertiary', className)}>Stage</span>
    );
  }

  const pillContent = (
    <>
      {!muted && (
        <Dot size='sm' style={{ color: stageColor }} aria-hidden='true' />
      )}
      {stageLabel}
    </>
  );
  const pillClassName = cx(
    'inline-flex items-center gap-1.5 rounded-full border',
    sizeClasses[size],
    muted && 'bg-bg-primary text-fg-tertiary border-border-secondary',
    className
  );

  if (pipelineRoute) {
    return (
      <Link
        to={pipelineRoute}
        title={`Open ${stageLabel} in the pipeline`}
        onClick={stopClickPropagation}
        className={cx(
          pillClassName,
          'transition-opacity hover:opacity-80 cursor-pointer'
        )}
        style={badgeStyle}
      >
        {pillContent}
      </Link>
    );
  }

  return (
    <span className={pillClassName} style={badgeStyle}>
      {pillContent}
    </span>
  );
};
